import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Chip,
    Divider,
} from "@mui/material";
import { LocalShipping, Inventory2, VerifiedUser, CheckCircle, FitnessCenter } from "@mui/icons-material";
import systemSettingsService from "../../services/systemSettingsService";
import PageHeader from "../../components/common/PageHeader";
import SkeletonLoader from "../../components/common/SkeletonLoader";

const CustomerRates = () => {
    const [rates, setRates] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        systemSettingsService
            .getRates()
            .then(({ data }) => setRates(data.data))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <PageHeader title="Shipping Rates" subtitle="Flat rate packages for online shoppers" />
                <SkeletonLoader rows={4} />
            </Box>
        );
    }

    if (!rates) return null;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Shipping Rates"
                subtitle="Flat rate packages for online shoppers — no hidden fees"
            />

            {/* Rate tier cards */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: `repeat(${rates.rateTiers.length}, 1fr)`,
                    },
                    gap: 2.5,
                }}
            >
                {rates.rateTiers.map((tier) => (
                    <Card
                        key={tier.name}
                        sx={{
                            position: "relative",
                            border: tier.highlight ? "2px solid" : "1px solid",
                            borderColor: tier.highlight ? "primary.main" : "divider",
                        }}
                    >
                        {tier.highlight && tier.highlightLabel && (
                            <Chip
                                label={tier.highlightLabel}
                                size="small"
                                sx={{
                                    position: "absolute",
                                    top: 12,
                                    right: 12,
                                    bgcolor: "primary.main",
                                    color: "white",
                                    fontWeight: 700,
                                    fontSize: 10,
                                }}
                            />
                        )}
                        <CardContent sx={{ p: 3 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    bgcolor: "info.light",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 2,
                                }}
                            >
                                <Inventory2 sx={{ color: "primary.main", fontSize: 20 }} />
                            </Box>

                            <Typography variant="overline" color="text.secondary" display="block">
                                {tier.name}
                            </Typography>
                            <Typography variant="h3" fontWeight={700} color="primary.main" mb={2}>
                                ${tier.price?.toFixed(2)}
                            </Typography>

                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Package size
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        Up to {tier.maxCbf} CF
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Consolidation
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {tier.consolidation}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Insurance
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {tier.insuranceLabel}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* Delivery fee + perks row */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2.5,
                }}
            >
                <Card>
                    <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocalShipping sx={{ color: "primary.main", fontSize: 18 }} />
                            <Typography variant="overline" color="text.secondary">
                                Local Delivery Fees
                            </Typography>
                        </Box>
                        {(rates.deliveryFees?.length
                            ? rates.deliveryFees
                            : [{ route: rates.deliveryRoute, fee: rates.deliveryFee }]
                        ).map((d, idx) => (
                            <Box
                                key={idx}
                                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {d.route}
                                </Typography>
                                <Typography fontWeight={700} fontSize={16}>
                                    ${d.fee?.toFixed(2)}
                                </Typography>
                            </Box>
                        ))}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                            <VerifiedUser sx={{ color: "primary.main", fontSize: 18 }} />
                            <Typography variant="overline" color="text.secondary">
                                What's Included
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 1,
                            }}
                        >
                            {rates.perks.map((perk) => (
                                <Box key={perk} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <CheckCircle sx={{ fontSize: 14, color: "success.main", flexShrink: 0 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {perk}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {rates.heavyHandlingFee > 0 && (
                <Card>
                    <CardContent sx={{ p: 3, display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap" }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: "error.light",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <FitnessCenter sx={{ color: "error.main", fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="overline" color="text.secondary" display="block">
                                Heavy Package Handling
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Packages over <strong>{rates.heavyWeightThresholdLb} lbs</strong> incur an additional
                                handling fee.
                            </Typography>
                        </Box>
                        <Typography fontWeight={700} fontSize={20} color="error.main" sx={{ flexShrink: 0 }}>
                            ${rates.heavyHandlingFee?.toFixed(2)}
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default CustomerRates;